import React, { useRef, useState } from 'react';
import { Modal, Collapse, Carousel, Tooltip } from 'antd';
import { fakeProImage } from '../../config/fakeData';
import { InfoCircleOutlined } from '@ant-design/icons';
import { ModalCarouselWrapper } from "./style";
import { AddCommas, primaryColor } from '../../config';
import { Link } from 'react-router-dom';
import { useThemeMode } from '../../contexts/ThemeContext';

const AMAZON_BASE_URL = "https://www.amazon.in/dp/";

const SLIDE_LABELS = ['Performance', 'Cost', 'Profit'];

const ProductDetailsModal = ({ open, onClose, data, currencySign }) => {
    const { themeMode } = useThemeMode();
    const isDark = themeMode === 'dark';
    // AddCommas function: Adds comma separators in Indian format
  

    const getAmount = (val) => {
        const amount = parseFloat(val || 0);
        const formattedNumber = AddCommas(Math.abs(amount).toFixed(2));
        return `${amount < 0 ? '-' : ''}${currencySign}${formattedNumber}`;
    };

    const getNestedValue = (row, path) => {
        if (!row) return undefined;
        if (Array.isArray(path)) {
            return path.reduce((acc, key) => (acc == null ? undefined : acc[key]), row);
        }
        return row[path];
    };

    const formatComparePercent = (value) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return null;
        return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
    };

    const renderCompareLine = (compareKey, valuePath, formatter = (value) => value) => {
        const compareWith = data?.compare_with;
        const compareRow = compareWith?.row;
        if (!compareWith || !compareRow) return null;

        const previousValue = getNestedValue(compareRow, valuePath);
        const hasPreviousValue = previousValue !== undefined && previousValue !== null && previousValue !== '';
        const pctText = formatComparePercent(compareKey ? compareWith?.change_percent?.[compareKey] : undefined);
        if (!hasPreviousValue && !pctText) return null;

        const pctValue = Number(compareKey ? compareWith?.change_percent?.[compareKey] : undefined);
        const pctColor = Number.isFinite(pctValue)
            ? pctValue >= 0
                ? (isDark ? '#36d399' : '#1a7f37')
                : (isDark ? '#ff6b81' : '#cf1322')
            : 'var(--app-text-muted)';

        return (
            <span
                className="fs-9 fw-semibold mt-1"
                style={{ color: 'var(--app-text-muted)', lineHeight: 1.2, whiteSpace: 'nowrap' }}
                title={compareWith?.start_date && compareWith?.end_date ? `${compareWith.start_date} to ${compareWith.end_date}` : undefined}
            >
                vs {hasPreviousValue ? formatter(previousValue) : 'prior period'}
                {pctText ? <span className="ms-1" style={{ color: pctColor }}>{pctText}</span> : null}
            </span>
        );
    };

    const mainCollapseRef = useRef(null);
    const carouselRef     = useRef(null);
    const [currentSlide, setCurrentSlide] = useState(0);

    const handleCollapseChange = (activeKeys) => {
        if (Array.isArray(activeKeys) && activeKeys.length > 0) {
            const newlyOpenedKey = activeKeys[activeKeys.length - 1];
            const panelHeader = mainCollapseRef.current?.querySelector(
                `.ant-collapse-item[data-key="${newlyOpenedKey}"] .ant-collapse-header`
            );

            if (panelHeader) {
                panelHeader.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                    inline: 'nearest'
                });
            }
        }
    };

    const generateMetricsCollapseItems = (metricsData) => {
        if (!metricsData) return { costMetrics: [], profitMetrics: [], performanceMetrics: [] };

        const costMetrics = [
            {
                key: '1',
                title: 'Amazon Fees',
                amount: getAmount(metricsData.more_data.amazon_fees.total),
                compare: renderCompareLine('fees', ['more_data', 'amazon_fees', 'total'], getAmount),
                items: Object.entries(metricsData.more_data.amazon_fees)
                    .filter(([key]) => key !== 'total')
                    .map(([key, value]) => ({
                        label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                        value: getAmount(value),
                        compare: renderCompareLine(key, ['more_data', 'amazon_fees', key], getAmount)
                    }))
            },
            {
                key: '2',
                title: 'Advertising Cost',
                amount: getAmount(-metricsData.more_data.advertising_cost.total),
                compare: renderCompareLine('ad_spend', ['more_data', 'advertising_cost', 'total'], (value) => getAmount(-value)),
                items: [
                    { label: 'Sponsored Product', value: getAmount(-metricsData.more_data.advertising_cost.sp_cost), compare: renderCompareLine('sp_cost', ['more_data', 'advertising_cost', 'sp_cost'], (value) => getAmount(-value)) },
                    { label: 'Sponsored Brand', value: getAmount(-metricsData.more_data.advertising_cost.sb_cost), compare: renderCompareLine('sb_cost', ['more_data', 'advertising_cost', 'sb_cost'], (value) => getAmount(-value)) },
                    { label: 'Sponsored Display', value: getAmount(-metricsData.more_data.advertising_cost.sd_cost), compare: renderCompareLine('sd_cost', ['more_data', 'advertising_cost', 'sd_cost'], (value) => getAmount(-value)) }
                ]
            },
            {
                key: '3',
                title: 'Promo',
                amount: getAmount(metricsData.more_data.promo),
                compare: renderCompareLine('promo', ['more_data', 'promo'], getAmount),
                items: []
            },
            {
                key: '4',
                title: 'Gift wrap',
                amount: getAmount(-metricsData.more_data.giftwrap),
                compare: renderCompareLine('giftwrap', ['more_data', 'giftwrap'], (value) => getAmount(-value)),
                items: []
            },
            {
                key: '5',
                title: 'Shipping Cost',
                amount: getAmount(-metricsData.more_data.shipping_cost.total),
                compare: renderCompareLine('shipping_cost', ['more_data', 'shipping_cost', 'total'], (value) => getAmount(-value)),
                items: []
            },
            {
                key: '6',
                title: (
                    <div className="d-flex align-items-center">
                        Refund cost
                        {!!metricsData.more_data.refund_cost.total && (
                            <Tooltip
                                placement="top"
                                title={
                                    <div className="p-1">
                                        <div className="d-flex justify-content-between mb-1" style={{ minWidth: '160px' }}>
                                            <span>Refunded amount:</span>
                                            <span className="fw-bold ms-4">{getAmount(metricsData.more_data.refund_cost.refunded_amount)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-1">
                                            <span>Fixed closing fee:</span>
                                            <span className="fw-bold ms-4">{getAmount(metricsData.more_data.refund_cost.fixed_closing_fee)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span>Ship promotion:</span>
                                            <span className="fw-bold ms-4">{getAmount(metricsData.more_data.refund_cost.ship_promotion)}</span>
                                        </div>
                                    </div>
                                }
                            >
                                <InfoCircleOutlined className="ms-2 text-gray-500" style={{ fontSize: '13px', cursor: 'help' }} />
                            </Tooltip>
                        )}
                    </div>
                ),
                amount: getAmount(metricsData.more_data.refund_cost.total),
                compare: renderCompareLine('refund_cost', ['more_data', 'refund_cost', 'total'], getAmount),
                items: []
            },
            {
                key: '7',
                title: 'Cost of Goods',
                amount: getAmount(-metricsData.more_data.cost_of_goods.total),
                compare: renderCompareLine('cogs', ['more_data', 'cost_of_goods', 'total'], (value) => getAmount(-value)),
                items: [{ label: 'COGS', value: getAmount(-metricsData.more_data.cost_of_goods.cost_of_goods_sold), compare: renderCompareLine('cogs', ['more_data', 'cost_of_goods', 'cost_of_goods_sold'], (value) => getAmount(-value)) }]
            },
            {
                key: '8',
                title: 'Expenses',
                amount: getAmount(-metricsData.more_data.expenses.total),
                compare: renderCompareLine('expenses', ['more_data', 'expenses', 'total'], (value) => getAmount(-value)),
                items: Object.entries(metricsData.more_data.expenses)
                    .filter(([key]) => key !== 'total')
                    .map(([key, value]) => ({
                        label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                        value: getAmount(-value),
                        compare: renderCompareLine(key, ['more_data', 'expenses', key], (prevValue) => getAmount(-prevValue))
                    }))
            },
            {
                key: '9',
                title: 'Net Tax',
                amount: getAmount(metricsData.more_data.tax_liability.total),
                compare: renderCompareLine('tax', ['more_data', 'tax_liability', 'total'], getAmount),
                items: [
                    { label: 'Tax Collected', value: getAmount(metricsData.more_data.tax_liability.total_tax_collected), compare: renderCompareLine('total_tax_collected', ['more_data', 'tax_liability', 'total_tax_collected'], getAmount) },
                    {
                        label: (
                            <div className="d-flex align-items-center">
                                Tax Paid
                                {metricsData.more_data.tax_liability.tax_paid_breakdown && (
                                    <Tooltip
                                        placement="top"
                                        title={
                                            <div className="p-1">
                                                <div className="d-flex justify-content-between mb-1" style={{ minWidth: '160px' }}>
                                                    <span>Tax paid on COGS:</span>
                                                    <span className="fw-bold ms-4">{getAmount(metricsData.more_data.tax_liability.tax_paid_breakdown.cogs)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between mb-1">
                                                    <span>Tax paid on Amazon fee:</span>
                                                    <span className="fw-bold ms-4">{getAmount(metricsData.more_data.tax_liability.tax_paid_breakdown.fees)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between">
                                                    <span>Tax paid on Ads:</span>
                                                    <span className="fw-bold ms-4">{getAmount(metricsData.more_data.tax_liability.tax_paid_breakdown.advertising)}</span>
                                                </div>
                                            </div>
                                        }
                                    >
                                        <InfoCircleOutlined className="ms-2 text-gray-500" style={{ fontSize: '13px', cursor: 'help' }} />
                                    </Tooltip>
                                )}
                            </div>
                        ),
                        value: getAmount(metricsData.more_data.tax_liability.total_tax_paid),
                        compare: renderCompareLine('total_tax_paid', ['more_data', 'tax_liability', 'total_tax_paid'], getAmount)
                    }
                ]
            }
        ];

        const profitMetrics = [
            { key: '1', title: 'Gross Profit', amount: getAmount(metricsData.more_data.gross_profit), compare: renderCompareLine('gross_profit', ['more_data', 'gross_profit'], getAmount), items: [] },
            { key: '2', title: 'Net Profit', amount: getAmount(metricsData.more_data.net_profit), compare: renderCompareLine('net_profit', ['more_data', 'net_profit'], getAmount), items: [] }
        ];

        const performanceMetrics = [
            {
                key: '1',
                title: 'Total Sales',
                amount: getAmount(metricsData.more_data.sales.total),
                compare: renderCompareLine('sales', ['more_data', 'sales', 'total'], getAmount),
                items: [
                    { label: 'Organic', value: getAmount(metricsData.more_data.sales.organic), compare: renderCompareLine('organic_sales', ['more_data', 'sales', 'organic'], getAmount) },
                    { label: 'Sponsored Product', value: getAmount(metricsData.more_data.sales.sp_sales), compare: renderCompareLine('sp_sales', ['more_data', 'sales', 'sp_sales'], getAmount) },
                    { label: 'Sponsored Brand', value: getAmount(metricsData.more_data.sales.sb_sales), compare: renderCompareLine('sb_sales', ['more_data', 'sales', 'sb_sales'], getAmount) },
                    { label: 'Sponsored Display', value: getAmount(metricsData.more_data.sales.sd_sales), compare: renderCompareLine('sd_sales', ['more_data', 'sales', 'sd_sales'], getAmount) }
                ]
            },
            {
                key: '2',
                title: 'Advertising clicks',
                amount: AddCommas(metricsData.more_data.advertising_clicks.total || 0),
                compare: renderCompareLine('advertising_clicks', ['more_data', 'advertising_clicks', 'total'], (value) => AddCommas(value || 0)),
                items: [
                    { label: 'Sponsored Product', value: AddCommas(metricsData.more_data.advertising_clicks.sp_click || 0), compare: renderCompareLine('sp_click', ['more_data', 'advertising_clicks', 'sp_click'], (value) => AddCommas(value || 0)) },
                    { label: 'Sponsored Brand', value: AddCommas(metricsData.more_data.advertising_clicks.sb_click || 0), compare: renderCompareLine('sb_click', ['more_data', 'advertising_clicks', 'sb_click'], (value) => AddCommas(value || 0)) },
                    { label: 'Sponsored Display', value: AddCommas(metricsData.more_data.advertising_clicks.sd_click || 0), compare: renderCompareLine('sd_click', ['more_data', 'advertising_clicks', 'sd_click'], (value) => AddCommas(value || 0)) },
                ]
            },
       
            {
                key: '3',
                title: 'Total units',
                amount: AddCommas(metricsData.more_data.units.total || 0),
                compare: renderCompareLine('total_units', ['more_data', 'units', 'total'], (value) => AddCommas(value || 0)),
                items: [
                    { label: 'Organic', value: AddCommas(metricsData.more_data.units.organic || 0), compare: renderCompareLine('units', ['more_data', 'units', 'organic'], (value) => AddCommas(value || 0)) },
                    { label: 'Sponsored Product', value: AddCommas(metricsData.more_data.units.sp_units || 0), compare: renderCompareLine('sp_units', ['more_data', 'units', 'sp_units'], (value) => AddCommas(value || 0)) },
                    { label: 'Sponsored Brand', value: AddCommas(metricsData.more_data.units.sb_units || 0), compare: renderCompareLine('sb_units', ['more_data', 'units', 'sb_units'], (value) => AddCommas(value || 0)) },
                    { label: 'Sponsored Display', value: AddCommas(metricsData.more_data.units.sd_units || 0), compare: renderCompareLine('sd_units', ['more_data', 'units', 'sd_units'], (value) => AddCommas(value || 0)) },
                ]
            },
       
            {
                key: '4',
                title: (
                    <div className="d-flex align-items-center">
                        Ad conversion
                        <Tooltip title="(SP Units + SB Units + SD Units) / Total Ad Clicks X 100" placement="top">
                            <InfoCircleOutlined className="ms-2 text-gray-500" style={{ fontSize: '14px', cursor: 'help' }} />
                        </Tooltip>
                    </div>
                ),
                amount: `${metricsData.more_data.advertising_clicks.total > 0
                    ? (
                        ((metricsData.more_data.units.sp_units + metricsData.more_data.units.sb_units + metricsData.more_data.units.sd_units || 0) / metricsData.more_data.advertising_clicks.total) * 100
                    ).toFixed(2)
                    : 0}%`,
                compare: null,
                items: []
            },
            { key: '5', title: 'Refund units', amount: AddCommas(metricsData.more_data.refund_units || 0), compare: renderCompareLine('refund_unit', ['more_data', 'refund_units'], (value) => AddCommas(value || 0)), items: [] },
       
            { key: '6', title: 'Refunds %', amount: `${metricsData.more_data.refunds_percent}%`, compare: renderCompareLine('refunds_percent', ['more_data', 'refunds_percent'], (value) => `${value ?? 0}%`), items: [] }
        ];

        const formatSections = (items) =>
            items.map((section) => ({
                key: section.key,
                label: (
                    <div className="d-flex justify-content-between w-100">
                        <span className="text-gray-800 fw-bold fs-6">
                            {section.title}
                        </span>
                        <span className="text-gray-800 fw-bolder fs-6 me-3 gotham_black d-flex flex-column align-items-end">
                            <span>{section.amount}</span>
                            {section.compare}
                        </span>
                    </div>
                ),
                children: section.items.length > 0 ? (
                    <>
                        {section.items.map((item, i) => (
                            <React.Fragment key={i}>
                                <div className="row g-4 mt-0 ps-9 pe-5">
                                    <div className="col w-md-100 d-flex align-items-center justify-content-between pe-md-0 mt-1">
                                        <h3 className="mb-1 fw-semibold text-gray-700 fs-6 text-dark">{item.label}</h3>
                                        <h3 className="fs-6 text-gray-800 fw-bolder mb-0 d-flex flex-column align-items-end">
                                            <span>{item.value}</span>
                                            {item.compare}
                                        </h3>
                                    </div>
                                </div>
                                {i < section.items.length - 1 && (
                                    <div className="separator separator-dashed border-gray-300 my-2" />
                                )}
                            </React.Fragment>
                        ))}
                    </>
                ) : null,
                showArrow: section.items.length > 0,
                collapsible: section.items.length > 0 ? '' : 'disabled',
                className: section.items.length === 0 ? 'ant-collapse-item-no-child-padding' : ''
            }));

        return {
            costMetrics: formatSections(costMetrics),
            profitMetrics: formatSections(profitMetrics),
            performanceMetrics: formatSections(performanceMetrics),
        };
    };

    const { costMetrics, profitMetrics, performanceMetrics } = generateMetricsCollapseItems(data);

    return (
        <ModalCarouselWrapper>
            <Modal
                open={open}
                onCancel={onClose}
                footer={null}
                width={700}
                centered
                className="custom-modal"
                styles={{
                    body: {
                        height: '85vh',
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                {data && (
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

                        {/* 1. FIXED MODAL HEADER */}
                        <div style={{ padding: '24px 24px 10px 24px', flexShrink: 0 }}>
                            <div style={{ textAlign: 'center', marginBottom: 15 }}>
                                <img src={data?.image_url || fakeProImage} alt="Product" style={{
                                    maxHeight: 100,
                                    objectFit: 'contain',
                                }}/>
                            </div>
                            <h2 style={{ textAlign: 'center', marginBottom: 8, fontSize: '1.1rem' }}>
                                <Link
                                    href={data?.asin ? `${AMAZON_BASE_URL}${data.asin}` : "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ lineHeight:1.6, textDecoration: 'none' }}
                                    className='text-dark text-hover-danger'
                                >
                                    {data?.product_title}
                                </Link>
                            </h2>
                            <div style={{ textAlign: 'center', marginBottom: 15 }}>
                                <span style={{ color: 'var(--app-text-muted)', marginRight: 15 }}><b>ASIN:</b> {data?.asin}</span>
                                <span style={{ color: 'var(--app-text-muted)' }}><b>SKU:</b> {data?.sku}</span>
                            </div>
                        </div>

                        {/* 2. CENTER SECTION: Tabs + Carousel */}
                        <div style={{ flex: 1, padding: '0 24px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            {/* Tab navigation — synced with carousel */}
                            <div style={{ display: 'flex', gap: 6, width: '100%', paddingBottom: 2, marginBottom: 12, flexShrink: 0 }}>
                                {SLIDE_LABELS.map((label, i) => {
                                    const isActive = i === currentSlide;
                                    return (
                                        <button
                                            key={label}
                                            onClick={() => carouselRef.current?.goTo(i)}
                                            aria-label={`Show ${label}`}
                                            title={label}
                                            style={{
                                                flex: 1,
                                                padding: '7px 8px',
                                                border: `1.5px solid ${isActive ? primaryColor : 'var(--app-border)'}`,
                                                borderRadius: 8,
                                                background: isActive ? primaryColor : 'transparent',
                                                color: isActive ? '#ffffff' : 'var(--app-text-muted)',
                                                fontSize: 12.5,
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                outline: 'none',
                                                whiteSpace: 'nowrap',
                                                textAlign: 'center',
                                                boxShadow: isActive ? `0 3px 10px ${primaryColor}59` : 'none',
                                                transition: 'background 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                                            }}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>

                            <Carousel
                                ref={carouselRef}
                                effect="scrollx"
                                className="metrics-modal-carousel"
                                dots={false}
                                afterChange={(index) => setCurrentSlide(index)}
                                style={{ height: '100%' }}
                            >
                                {/* Performance Slide */}
                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <div className="modal-collapse-scrollable" style={{ overflowY: 'auto', maxHeight: 'calc(85vh - 420px)', scrollbarWidth: 'thin' }}>
                                        <Collapse items={performanceMetrics} bordered={false} onChange={handleCollapseChange} ref={mainCollapseRef} />
                                    </div>
                                </div>

                                {/* Cost Slide */}
                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <div className="modal-collapse-scrollable" style={{ overflowY: 'auto', maxHeight: 'calc(85vh - 420px)', scrollbarWidth: 'thin' }}>
                                        <Collapse items={costMetrics} bordered={false} onChange={handleCollapseChange} ref={mainCollapseRef} />
                                    </div>
                                </div>

                                {/* Profit Slide */}
                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <div className="modal-collapse-scrollable" style={{ overflowY: 'auto', maxHeight: 'calc(85vh - 420px)', scrollbarWidth: 'thin' }}>
                                        <Collapse items={profitMetrics} bordered={false} onChange={handleCollapseChange} ref={mainCollapseRef} />
                                    </div>
                                </div>
                            </Carousel>
                        </div>

                        {/* 3. FIXED MODAL FOOTER */}
                        <div style={{ padding: '20px 24px', flexShrink: 0, background: 'var(--app-surface)' }}>
                            <div style={{
                                padding: '12px 20px',
                                border: `1px dashed ${isDark ? 'rgba(54, 211, 153, 0.45)' : '#63CE94'}`,
                                borderRadius: '8px',
                                background: isDark ? 'rgba(15, 126, 62, 0.18)' : '#63ce9440',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <h5 style={{ marginBottom: 0, color: isDark ? '#36d399' : '#0f7e3e', fontWeight: 600, fontSize: '1.2rem' }}>Net Profit</h5>
                                <div style={{ textAlign: 'right' }}>
                                    <h4 style={{ marginBottom: 0, fontWeight: 800, color: isDark ? '#36d399' : '#0f7e3e', fontSize: '1.8rem' }}>
                                        {getAmount(data?.net_profit)}
                                    </h4>
                                    {renderCompareLine('net_profit', 'net_profit', getAmount)}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </ModalCarouselWrapper>
    );
};

export default ProductDetailsModal;


